import { z } from 'zod';
import { BUSINESS_TYPES, MODULE_KEYS } from './modules.js';
import { CURRENCY_CODES } from './money.js';
import { ALL_PERMISSIONS } from './permissions.js';

/** Request/response validation shared by the API (authoritative) and the web forms. */

export const PASSWORD_MIN_LENGTH = 8;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(128, 'Use at most 128 characters')
  .refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), 'Include at least one letter and one number');

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'At least 3 characters')
  .max(40)
  .regex(/^[a-z0-9._-]+$/, 'Letters, numbers, dots, dashes and underscores only');

export const loginSchema = z.object({
  username: z.string().trim().toLowerCase().min(1, 'Enter your username').max(40),
  password: z.string().min(1, 'Enter your password').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
  })
  .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'The new password must be different',
    path: ['newPassword'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

const phoneSchema = z
  .string()
  .trim()
  .max(20)
  .regex(/^\+?[0-9 ()-]*$/, 'Use digits only, e.g. +263 77 123 4567')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();

export const createUserSchema = z.object({
  fullName: trimmed(80).min(2, 'Enter the full name'),
  username: usernameSchema,
  email: z
    .string()
    .trim()
    .toLowerCase()
    .max(120)
    .transform((v) => (v === '' ? null : v))
    .pipe(z.email('Enter a valid email').nullable())
    .nullable()
    .optional(),
  phone: phoneSchema,
  roleId: z.uuid('Choose a role'),
  branchIds: z.array(z.uuid()).min(1, 'Choose at least one branch'),
  defaultBranchId: z.uuid(),
  password: passwordSchema,
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserSchema = createUserSchema
  .omit({ password: true, username: true })
  .extend({ isActive: z.boolean() });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const resetPasswordSchema = z.object({ password: passwordSchema });

export const roleSchema = z.object({
  name: trimmed(40).min(2, 'Enter a role name'),
  description: trimmed(200).default(''),
  permissions: z.array(z.enum(ALL_PERMISSIONS as [string, ...string[]])).max(ALL_PERMISSIONS.length),
});
export type RoleInput = z.infer<typeof roleSchema>;

export const branchSchema = z.object({
  name: trimmed(80).min(2, 'Enter the branch name'),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(8)
    .regex(/^[A-Z0-9]+$/, 'Letters and numbers only'),
  address: optionalText(200),
  phone: phoneSchema,
  isActive: z.boolean().default(true),
});
export type BranchInput = z.infer<typeof branchSchema>;

export const businessSettingsSchema = z
  .object({
    name: trimmed(100).min(2, 'Enter the registered business name'),
    tradingName: optionalText(100),
    businessType: z.enum(BUSINESS_TYPES),
    tin: z
      .string()
      .trim()
      .regex(/^\d{10}$|^$/, 'ZIMRA TIN is 10 digits')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    vatRegistered: z.boolean(),
    vatNumber: z
      .string()
      .trim()
      .regex(/^\d{9}$|^$/, 'VAT number is 9 digits')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    standardVatRateBp: z.number().int().min(0).max(5000),
    baseCurrency: z.enum(CURRENCY_CODES as [string, ...string[]]),
    acceptedCurrencies: z.array(z.enum(CURRENCY_CODES as [string, ...string[]])).min(1),
    address: optionalText(200),
    phone: phoneSchema,
    email: z
      .string()
      .trim()
      .max(120)
      .transform((v) => (v === '' ? null : v))
      .pipe(z.email('Enter a valid email').nullable())
      .nullable()
      .optional(),
    receiptHeader: optionalText(300),
    receiptFooter: optionalText(300),
    moduleOverrides: z.partialRecord(z.enum(MODULE_KEYS), z.boolean()).default({}),
    allowNegativeStock: z.boolean().default(false),
    scaleConfig: z
      .object({
        prefixes: z.array(z.string().regex(/^2\d$/, 'Scale prefixes are 20–29')).min(1).max(10),
        pluLength: z.number().int().min(4).max(6),
        mode: z.enum(['weight', 'price']),
      })
      .nullable()
      .optional(),
  })
  .refine((v) => v.acceptedCurrencies.includes(v.baseCurrency), {
    message: 'The base currency must be one of the accepted currencies',
    path: ['acceptedCurrencies'],
  })
  .refine((v) => !v.vatRegistered || !!v.vatNumber, {
    message: 'Enter the VAT number for a VAT-registered business',
    path: ['vatNumber'],
  });
export type BusinessSettingsInput = z.infer<typeof businessSettingsSchema>;

export const auditQuerySchema = z.object({
  action: z.string().max(60).optional(),
  userId: z.uuid().optional(),
  entityType: z.string().max(40).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: z.coerce.number().int().positive().optional(),
});
export type AuditQuery = z.infer<typeof auditQuerySchema>;
